import { EImportFileType } from 'core/model/enums';
import type {
  IBranch,
  IBranchesList,
  IToggleBranchCreditParams,
  IToggleBranchPurchaseLimitParams,
  IToggleBranchUserDataUpdateParams,
  IUpsertBranchClientExclusionParams,
  IUpsertBranchParams,
} from 'core/model/interfaces/distributors/branches.interface';
import { toPriceGroupList } from '../master-entities/master-entities.mappers';
import type {
  IBranchDTO,
  IBranchPurchaseLimitDTO,
  IBranchScheduleDTO,
  IGetBranchesListResponse,
  IToggleBranchCreditDTO,
  IToggleBranchCreditParamsDTO,
  IToggleBranchPurchaseLimitParamsDTO,
  IToggleBranchUserDataUpdateParamsDTO,
  IUpdateBranchParamsDTO,
} from './branches.dto';

export const mapBranchItem = (item: IBranchDTO): IBranch => {
  const branch: IBranch = {
    branchId: item.id,
    branchName: item.name,
    distributor: {
      distributorId: item.distributor.id,
      distributorName: item.distributor.name,
    },
    schedules: item.schedules.map(schedule => ({
      weekday: schedule.weekday,
      time: schedule.time,
    })),
    purchaseLimits: item.purchaseLimits || [],
    credit: {
      enabled: item.credit.enabled,
      priceGroups: toPriceGroupList(item.credit.priceGroups),
    },
    userDataUpdate: {
      enabled: item.updateMandatory?.enabled || false,
    },
  };

  return branch;
};

/**
 * @todo Remove the `purchaseLimit` validation when backend confirms that the field is always returned with data.
 */
export const mapDtoToBranches = (response: IGetBranchesListResponse): IBranchesList => {
  const list: IBranchesList = {
    branches: response.branches.map(mapBranchItem),
    pagination: response.pagination,
  };

  return list;
};

export const toBranchDto = (data: IUpsertBranchParams): IUpdateBranchParamsDTO => {
  const schedules = data.schedules.map(
    (schedule): IBranchScheduleDTO => ({
      weekday: schedule.weekday,
      time: schedule.time,
    }),
  );

  const purchaseLimits = data.purchaseLimits.map(
    (limit): IBranchPurchaseLimitDTO => ({
      channelId: limit.channelId,
      limit: limit.limit ? Number(limit.limit) : null,
      enabled: limit.enabled,
    }),
  );

  const credit: IToggleBranchCreditDTO = {
    enabled: data.credit.enabled,
    priceListIds: data.credit.priceListIds.map(Number),
  };

  const branch: IUpdateBranchParamsDTO = {
    branchId: data.branchId,
    distributorId: data.distributorId,
    schedules,
    purchaseLimits,
    credit,
  };

  return branch;
};

export const toTogglePurchaseLimitDto = (
  data: IToggleBranchPurchaseLimitParams,
): IToggleBranchPurchaseLimitParamsDTO => {
  const { branch, purchaseLimit } = data;

  const purchaseLimits = branch.purchaseLimits.map(
    (limit): IBranchPurchaseLimitDTO => ({
      channelId: limit.channelId,
      limit: limit.limit ? Number(limit.limit) : null,
      enabled: purchaseLimit.channelId === limit.channelId ? !limit.enabled : limit.enabled,
    }),
  );

  const updatedBranch: IToggleBranchPurchaseLimitParamsDTO = {
    branchId: branch.branchId,
    distributorId: branch.distributor.distributorId,
    purchaseLimits,
  };

  return updatedBranch;
};

export const toToggleCreditDto = (data: IToggleBranchCreditParams): IToggleBranchCreditParamsDTO => {
  const { branch, credit } = data;

  const priceListIds = credit.priceGroups
    .map(priceGroup => priceGroup.priceLists)
    .flat()
    .map(priceList => priceList.priceListId);

  const updatedCredit: IToggleBranchCreditParamsDTO = {
    branchId: branch.branchId,
    distributorId: branch.distributor.distributorId,
    credit: {
      enabled: !credit.enabled,
      priceListIds,
    },
  };

  return updatedCredit;
};

export const toToggleUserDataUpdateDto = (
  data: IToggleBranchUserDataUpdateParams,
): IToggleBranchUserDataUpdateParamsDTO => {
  const { branch, userDataUpdate } = data;

  const updatedCredit: IToggleBranchUserDataUpdateParamsDTO = {
    branchId: branch.branchId,
    distributorId: branch.distributor.distributorId,
    isUserMandatory: userDataUpdate.enabled,
  };

  return updatedCredit;
};

export const toUpsertBranchCustomersExclusion = (params: IUpsertBranchClientExclusionParams) => {
  const { file } = params;

  const formData = new FormData();

  formData.append('file', file.blob);

  formData.append('fileType', EImportFileType.EXCLUDE_CREDIT);

  return formData;
};
